import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { MonthYearPicker } from "@/components/common/MonthYearPicker";
import { TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import { getEffectiveTransactionDate } from '@/lib/utils';
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { CustomTooltipProps, TooltipPayload } from "@/types/recharts";

interface TransactionWithCategory {
  id: string;
  tipo: "entrada" | "saida";
  valor: number;
  data: string;
  foi_paga: boolean;
  ignorar_transacao: boolean;
  categoria?: {
    nome: string;
    cor: string;
  };
}

interface MonthlyBalanceReportProps {
  transactions: TransactionWithCategory[];
  selectedDate?: Date;
  onDateChange?: (date: Date) => void;
}

export function MonthlyBalanceReport({ 
  transactions, 
  selectedDate = new Date(),
  onDateChange 
}: MonthlyBalanceReportProps) {
  
  // Buscar contas para calcular datas efetivas
  const { data: accounts = [] } = useQuery({
    queryKey: ['contas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contas')
        .select('*')
        .eq('ativo', true);
      
      if (error) throw error;
      return data || [];
    }
  });
  
  const dailyBalanceData = useMemo(() => {
    const dailyBaseMonth = selectedDate ? new Date(selectedDate) : new Date();
    const dailyMonthStart = startOfMonth(dailyBaseMonth);
    const dailyMonthEnd = endOfMonth(dailyBaseMonth);
    const daysInMonth = eachDayOfInterval({ start: dailyMonthStart, end: dailyMonthEnd });
    
    const dailyTransactions = transactions.filter(t => {
      if (t.ignorar_transacao || !t.foi_paga) return false;
      // @ts-expect-error - TransactionWithCategory type compatible with Transaction
      const transactionDate = getEffectiveTransactionDate(t, accounts);
      return transactionDate && 
             transactionDate instanceof Date && 
             !isNaN(transactionDate.getTime()) && 
             transactionDate >= dailyMonthStart && 
             transactionDate <= dailyMonthEnd;
    });
    
    let runningBalance = 0;
    
    return daysInMonth.map(day => {
      const dayTransactions = dailyTransactions.filter(t => {
        // @ts-expect-error - TransactionWithCategory type compatible with Transaction
        const txDate = getEffectiveTransactionDate(t, accounts);
        return txDate && 
               txDate instanceof Date && 
               !isNaN(txDate.getTime()) && 
               isSameDay(txDate, day);
      });
      
      const dayBalance = dayTransactions.reduce((sum, t) => {
        return sum + (t.tipo === 'entrada' ? t.valor : -t.valor);
      }, 0);
      
      runningBalance += dayBalance;
      
      return {
        day: format(day, 'dd/MM', { locale: ptBR }),
        fullDate: format(day, 'dd/MM/yyyy', { locale: ptBR }),
        balance: runningBalance,
        dayChange: dayBalance
      };
    });
  }, [transactions, selectedDate, accounts]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry: TooltipPayload, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.dataKey === 'receitas' ? 'Receitas' : 
               entry.dataKey === 'despesas' ? 'Despesas' : 
               entry.dataKey === 'saldo' ? 'Saldo' : entry.dataKey}: {formatCurrency(entry.value as number)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const currentBalance = dailyBalanceData[dailyBalanceData.length - 1]?.balance || 0;
  const monthStart = dailyBalanceData[0]?.balance || 0;
  const monthChange = currentBalance - (dailyBalanceData[0]?.balance - dailyBalanceData[0]?.dayChange || 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base font-medium text-muted-foreground">
              Variação do Mês
            </CardTitle>
            <div className={`p-2 rounded-md ${
              monthChange >= 0 
                ? 'bg-success text-success-foreground' 
                : 'bg-destructive text-destructive-foreground'
            }`}>
              {monthChange >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
            </div>
          </CardHeader>
          <CardContent>
            <div className={`text-base font-bold ${
              monthChange > 0 ? 'text-success' : monthChange < 0 ? 'text-destructive' : 'text-muted-foreground'
            }`}>
              {formatCurrency(monthChange)}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {monthChange >= 0 ? 'Crescimento' : 'Redução'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Date Picker */}
      <div className="flex justify-center">
        <MonthYearPicker
          selectedDate={selectedDate}
          onDateChange={onDateChange || (() => {})}
        />
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Evolução Diária do Saldo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyBalanceData}>
                <defs>
                  <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="day" 
                  tick={{ fontSize: 12 }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickFormatter={formatCurrency}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="balance"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#balanceGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}